import { useState, useEffect } from 'react';
import type * as React from 'react';

const TOAST_LIMIT = 3;

export interface ToastData {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  variant?: 'default' | 'destructive';
  duration?: number;
  dismiss?: () => void;
}

type ToastInput = Omit<ToastData, 'id' | 'dismiss'>;

interface ToastState {
  toasts: ToastData[];
}

let count = 0;
function generateId(): string {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}

const toastStore = {
  state: { toasts: [] } as ToastState,
  listeners: [] as Array<(state: ToastState) => void>,
  getState: () => toastStore.state,
  setState(next: ToastState | ((prev: ToastState) => ToastState)) {
    toastStore.state = typeof next === 'function' ? next(toastStore.state) : next;
    toastStore.listeners.forEach((l) => l(toastStore.state));
  },
  subscribe(listener: (state: ToastState) => void) {
    toastStore.listeners.push(listener);
    return () => {
      toastStore.listeners = toastStore.listeners.filter((l) => l !== listener);
    };
  },
};

export function toast(props: ToastInput) {
  const id = generateId();
  const dismiss = () =>
    toastStore.setState((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  const update = (next: Partial<ToastInput>) =>
    toastStore.setState((state) => ({
      toasts: state.toasts.map((t) => (t.id === id ? { ...t, ...next } : t)),
    }));

  toastStore.setState((state) => ({
    toasts: [{ ...props, id, dismiss }, ...state.toasts].slice(0, TOAST_LIMIT),
  }));

  return { id, dismiss, update };
}

export function useToast() {
  const [state, setState] = useState<ToastState>(toastStore.getState());

  useEffect(() => toastStore.subscribe(setState), []);

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    state.toasts.forEach((t) => {
      if (t.duration === Infinity) return;
      timeouts.push(setTimeout(() => t.dismiss?.(), t.duration ?? 5000));
    });
    return () => timeouts.forEach(clearTimeout);
  }, [state.toasts]);

  return { toast, toasts: state.toasts };
}
